import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { GlobalEventType } from '@app/services/global-event.service';

export const BRIDGE_TYPE = GlobalEventType.IframeBridge;

export interface IframeBridgeMessage<T = unknown> {
  payload: T;
  /** The window that posted the message — used to send replies back. */
  source: Window;
}

export namespace IframeDialog {
  export enum Events {
    dialogResult = 'dialogResult',

    rejectionIssueModal = 'rejectionIssueModal',
    signatureIssuesModal = 'signatureIssuesModal',

    settingsDocumentRiskPortlet = 'settings-document-risk',
    settingsSiteRiskPortlet = 'settings-site-risk',

    settingsBridgeActivate = 'settingsBridgeActivate',
    settingsBridgeSaveRequest = 'settingsBridgeSaveRequest',
    settingsBridgeSaveDone = 'settingsBridgeSaveDone',
    settingsBridgeUndoRequest = 'settingsBridgeUndoRequest',
    settingsBridgeDirty = 'settingsBridgeDirty',
    settingsBridgeShowChangeLog = 'settingsBridgeShowChangeLog',

    settingsUnsavedChanges = 'settingsUnsavedChanges',
  }
}

export interface SettingsChangeLogSetting {
  settingName: string;
  settingTitle: string;
  convertData?: Record<string, string> | ((value: string) => string);
}

export interface SettingsChangeLogCfg {
  skipUpdateReason?: boolean;
  subsystemTypeId?: number;
  subsystemIds?: number[];
  siteSettings?: { ModuleName: string; SettingName: string }[];
  moduleName: string;
  settingTitle?: string;
  settings: SettingsChangeLogSetting[];
}

export interface SettingsBridgeActivatePayload {
  componentId: string;
  forceLoad?: boolean;
  searchText?: string;
  searchRegExpSource?: string;
  searchRegExpFlags?: string;
}

export interface SettingsBridgeRequestPayload {
  componentId: string;
}

export interface SettingsBridgeSaveDonePayload {
  componentId: string;
  success: boolean;
  error?: string;
}

export interface SettingsBridgeDirtyPayload {
  componentId: string;
  isModified: boolean;
}

export interface SettingsBridgeShowChangeLogPayload {
  componentId: string;
}
/**
 * Two-way message bridge between the Angular application and ExtJS UIs running
 * inside an <iframe>.
 *
 * ExtJS → Angular:
 *   window.parent.postMessage({ type: BRIDGE_TYPE, key, payload }, '*')
 *
 * Angular → ExtJS:
 *   iframeBridgeService.sendTo(sourceWindow, key, payload)
 *
 * Unlike GlobalEventService, this service preserves `event.source` so replies
 * can be routed back to the correct iframe window.
 */
@Injectable()
export class IframeBridgeService {
  private readonly _messages$ = new Subject<{ key: string; payload: unknown; source: Window }>();
  private readonly _messageListener = (event: MessageEvent) => this._onMessage(event);

  constructor() {
    window.addEventListener('message', this._messageListener, false);
  }

  /**
   * Returns an Observable that emits whenever an iframe posts a bridge message
   * with the given key. Emitted value includes the typed payload and the source
   * window so the caller can send a reply.
   */
  public on<T>(key: string): Observable<IframeBridgeMessage<T>> {
    return this._messages$.asObservable().pipe(
      filter((m) => m.key === key),
      map((m) => ({ payload: m.payload as T, source: m.source })),
    );
  }

  /**
   * Posts a message to the given target window (typically `event.source` from
   * an incoming message, or an iframe's `contentWindow`).
   */
  public sendTo(target: Window, key: string, payload: unknown, origin: string = '*'): void {
    target.postMessage({ type: BRIDGE_TYPE, key, payload }, origin);
  }

  private _onMessage(event: MessageEvent): void {
    if (event.data?.type === BRIDGE_TYPE && event.data?.key) {
      this._messages$.next({
        key: event.data.key,
        payload: event.data.payload,
        source: event.source as Window,
      });
    }
  }
}
